/* global __SHOW_VERSION_DIFF */
import angular from 'angular';

const packageJSON = require('../../package.json');

const _subscribers = [];
const RECHECK_INTERVAL = 5 * 60 * 1000; // 5 minutes
let _lastCheckTime = null;

export class appVersionService {
  constructor() {
    console.log('app.services.appVersionService created');
  }

  /**
   * Subscribe to version mismatch events
   * @param client
   */
  subscribe(client) {
    _subscribers.push(client);
  }

  /**
   * Event handler to notify event listeners when a version mismatch has occurred.
   * @param $event
   * @param locals
   */
  onVersionMismatch($event, locals) {
    _subscribers.forEach((subscriber) => {
      subscriber.notify($event, locals);
    });
  }

  /**
   * Returns the API and UI versions
   * @param {Object} response The http response object
   * @return {{api, ui}}
   */
  getVersions(response) {
    const apiBuild = _getApiBuildFromResponse(response);
    return _getVersions(apiBuild);
  }

  isUpToDate(versions) {
    return _isUpToDate(versions);
  }

  /**
   *
   * @param {{ api: {string}, ui: {string}}} versions A versions object
   * @param {String} requestUrl The url of the request;
   */
  notifyUserOnUpdate(versions, requestUrl) {
    _skipUpdateCheck(requestUrl)(
      _buffered()(
        () => {
          if (!_isUpToDate(versions)) {
            const { api, ui } = versions;
            this.onVersionMismatch(null, { apiVersion: api, uiVersion: ui, isUpToDate: false });
          }
        }
      )
    );
  }
}

appVersionService.$inject = [];

export default angular.module('app.components.global.appVersionDialog.appVersionService', [])
  .service('appVersionService', appVersionService)
  .name;

/** *********************************************
 * Private Functions
 */

function _getApiBuildFromResponse(response) {
  return response.headers('SS-theSafe-API-Version');
}

function _getVersions(apiBuild) {
  const uiBuild = packageJSON.version;
  const api = _parseVersion(apiBuild);
  const ui = _parseVersion(uiBuild);
  return { api, ui };
}

/**
 * Returns the "MAJOR.MINOR.REVISION" version and ignores BUILD
 * @param {string} version Expected format is "MAJOR.MINOR.REVISION" or "MAJOR.MINOR.REVISION.BUILD"
 * @return {string|null}
 */
function _parseVersion(version) {
  if (version && version.match) {
    const matches = version.match(/\d+\.\d+\.\d+/);
    if (Array.isArray(matches)) {
      return matches[0];
    }
  }
  // Version string is malformed
  return null;
}

/**
 * Curried function
 * @param {string} requestUrl
 * @return {Function}
 */
function _skipUpdateCheck(requestUrl) {
  return (next) => {
    const userCheckingForUpdates = requestUrl.endsWith('/test/today');
    if (userCheckingForUpdates) {
      return;
    }
    if (next) { next(); }
  };
}

/**
 * Curried function
 * @return {Function}
 */
function _buffered() {
  return (next) => {
    const currentCheckTime = Date.now();
    const checkInterval = currentCheckTime - _lastCheckTime;
    if (checkInterval < RECHECK_INTERVAL) {
      return;
    }
    if (next) { next(); }
  };
}

/**
 *
 * @param {{ api: {string}, ui: {string}}} versions A versions object
 */
function _isUpToDate({ api, ui }) {
  _lastCheckTime = Date.now();
  const versionLog = `(ui: "${ui}", api: "${api}")`;
  if (!api || !ui || !__SHOW_VERSION_DIFF) {
    // Something is wrong with the version strings, so fail silently (no version check)
    console.error('Could not determine theSafe version', versionLog);
    return true;
  }
  if (api !== ui) {
    console.warn('This version of theSafe is out of date. Please refresh your browser!', versionLog);
    return false;
  }
  console.log('You are on the current version of theSafe', versionLog);
  return true;
}
